/**
 * Custom LineChartComponent - Override Component
 * Reads raw responseList[0].responseData from the API response and
 * renders a multi-series ECharts line chart.
 */
import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as echarts from 'echarts';
import { Subject, takeUntil } from 'rxjs';
import { LineChartComponent as ParentLineChartComponent } from '@shared-lib/chart-components/line-chart/line-chart.component';
import { FormsModule } from '@angular/forms';
import { applyRoleVisibilityToOption } from '@shared-lib/utils/chart-role-visibility.utils';

@Component({
  selector: 'app-line-chart',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './line-chart.component.html',
  styleUrls: ['./line-chart.component.scss']
})
export class LineChartComponent extends ParentLineChartComponent implements OnDestroy {
  private static readonly PRICE_DYNAMICS_LEGEND_EVENT = 'price-dynamics-legend-sync';
  @ViewChild('chartHost', { static: false }) chartHost?: ElementRef<HTMLDivElement>;
  private destroy$ = new Subject<void>();
  private currentRequestPayload: any;
  startYear: number | null = null;
  endYear: number | null = null;
  yearOptions: number[] = [];
  private allYearOptions: number[] = [];
  private lastValidStartYear: number | null = null;
  private lastValidEndYear: number | null = null;
  private latestSeriesList: any[] = [];

  override ngAfterViewInit(): void {
    console.log('[CUSTOM LC] ngAfterViewInit');
    super.ngAfterViewInit(); // This enables global filter listening from the shared library
  }

  protected override loadAndRenderChart(filters?: any): any {
    console.log('[CUSTOM LC] loadAndRenderChart');
    const chartDom = this.chartHost?.nativeElement;
    if (!chartDom) {
      console.warn('[CUSTOM LC] chartDom not found for widgetId:', this.data?.widgetId);
      return;
    }

    if (!this.myChart) {
      const height = this.data.chartConfig?.height || this.data.height || '400px';
      chartDom.style.height = height;
      this.myChart = echarts.init(chartDom);
    }

    this.myChart.showLoading();

    const dataForRequest = JSON.parse(JSON.stringify(this.data || {}));
    const requestParams = filters?.requestParams || {};
    const hasNestedFilterObject =
      !!dataForRequest?.customPayload?.requestData?.filter &&
      typeof dataForRequest.customPayload.requestData.filter === 'object';

    if (hasNestedFilterObject) {
      Object.entries(requestParams).forEach(([key, value]) => {
        const normalizedValue =
          Array.isArray(value) && value.length === 1 ? value[0] : value;
        dataForRequest.customPayload.requestData.filter[key] = normalizedValue;
      });
    }

    this.currentRequestPayload = dataForRequest;

    const configReq = this.configService.getConfig(
      this.data.apiUrl,
      dataForRequest,
      { ...filters, useQueryParams: this.data?.useQueryParams || false },
      this.data.method
    ).pipe(takeUntil(this.destroy$));

    configReq.subscribe({
      next: (res: any) => {
        console.log('[CUSTOM LC] raw response:', res);

        const extractRows = (payload: any): any[] | null => {
          const nested = payload?.responseList?.[0]?.responseData;
          if (Array.isArray(nested)) {
            // Shape A: responseList[0].responseData = [{ data: [...], cumulativeSum: {...} }]
            if (Array.isArray(nested?.[0]?.data)) {
              return nested[0].data;
            }
            // Shape B: responseList[0].responseData = [{year, ...}, ...]
            if (nested.length > 0 && nested[0]?.year !== undefined) {
              return nested;
            }
          }

          const direct = payload?.responseData;
          if (Array.isArray(direct)) {
            // Shape C: responseData = [{ data: [...] }]
            if (Array.isArray(direct?.[0]?.data)) {
              return direct[0].data;
            }
            // Shape D: responseData = [{year, ...}, ...]
            if (direct.length > 0 && direct[0]?.year !== undefined) {
              return direct;
            }
          }

          return null;
        };

        const responseData = extractRows(res);

        if (responseData && responseData.length > 0) {
          const years = [...new Set(
            responseData
              .map((d: any) => Number(d?.year))
              .filter((y: number) => Number.isFinite(y))
          )].sort((a, b) => a - b);

          if (years.length > 0) {
            // Preserve a stable master year list so dropdown options don't shrink
            // when API returns a filtered subset.
            this.allYearOptions = [...new Set([...this.allYearOptions, ...years])]
              .sort((a, b) => a - b);
            this.yearOptions = [...this.allYearOptions];

            if (this.startYear === null || !this.yearOptions.includes(Number(this.startYear))) {
              this.startYear = this.yearOptions[0];
            }
            if (this.endYear === null || !this.yearOptions.includes(Number(this.endYear))) {
              this.endYear = this.yearOptions[this.yearOptions.length - 1];
            }

            if (this.startYear !== null && this.endYear !== null && this.startYear <= this.endYear) {
              this.lastValidStartYear = this.startYear;
              this.lastValidEndYear = this.endYear;
            }
          }

          this.renderChart(responseData);
        } else {
          // Fallback: try standard { category, value } shape
          console.warn('[CUSTOM LC] No responseData found, trying standard shape');
          this.renderChart(res);
        }
      },
      error: (err: any) => {
        console.error('[CUSTOM LC] error:', err);
        this.myChart?.hideLoading();
      }
    });

    return configReq;
  }

  onYearChange(): void {
    if (this.startYear === null || this.endYear === null) return;
    this.startYear = Number(this.startYear);
    this.endYear = Number(this.endYear);

    if (this.startYear > this.endYear) {
      console.warn('[CUSTOM LC] Invalid year range ignored:', { startYear: this.startYear, endYear: this.endYear });
      if (this.lastValidStartYear !== null && this.lastValidEndYear !== null) {
        this.startYear = this.lastValidStartYear;
        this.endYear = this.lastValidEndYear;
      }
      return;
    }

    this.lastValidStartYear = this.startYear;
    this.lastValidEndYear = this.endYear;

    const { startKey, endKey } = this.resolveYearFilterKeys();
    const listener =
      this.data?.filterListener || this.data?.filterListner || this.data?.widgetId;

    this.globalFilterService.updateFilterSelection(
      startKey,
      [this.formatYearValue(startKey, this.startYear, true)],
      false,
      undefined,
      false,
      listener,
    );

    this.globalFilterService.updateFilterSelection(
      endKey,
      [this.formatYearValue(endKey, this.endYear, false)],
      false,
      undefined,
      true,
      listener,
    );
  }

  getStartYearOptions(): number[] {
    if (this.endYear === null) {
      return this.yearOptions;
    }
    return this.yearOptions.filter((year) => year <= Number(this.endYear));
  }

  getEndYearOptions(): number[] {
    if (this.startYear === null) {
      return this.yearOptions;
    }
    return this.yearOptions.filter((year) => year >= Number(this.startYear));
  }

  private resolveYearFilterKeys(): { startKey: string; endKey: string } {
    const keys = this.data?.chartConfig?.filterKeys || {};
    const startKey = keys.start || keys.from || keys.fromYear || 'startDate';
    const endKey = keys.end || keys.to || keys.toYear || 'endDate';
    return { startKey, endKey };
  }

  private formatYearValue(key: string, year: number, isStart: boolean): string | number {
    if (String(key).toLowerCase().includes('date')) {
      return `${year}-${isStart ? '01-01' : '12-31'}`;
    }
    return year;
  }

  protected override renderChart(chartData?: any): any {
    console.log('[CUSTOM LC] renderChart', chartData);
    if (!this.myChart || !chartData) return;

    let xCategories: string[];
    let seriesList: any[];

    // Check if chartData is an array of { year, ppiData, competitorData } records
    if (Array.isArray(chartData) && chartData[0]?.year !== undefined) {
      xCategories = chartData.map((d: any) => `${d.year} ${d.period}`);

      // Shared label styles
      const endLabelStyle = {
        show: true,
        fontSize: 12,
        fontWeight: 'bold',
        color: 'inherit',
        position: 'right',
        verticalAlign: 'middle',
        distance: 8,
        formatter: (params: any) => params.value != null ? `${Number(params.value).toFixed(1)}%` : ''
      };

      const getNestedValue = (obj: any, path: string) => {
        return path.split('.').reduce((acc, part) => acc && acc[part], obj);
      };

      const originalMapping = this.data.chartConfig?.seriesMapping || [];
      const mapping = JSON.parse(JSON.stringify(originalMapping));
      const cphSeriesKey = 'priceDynamicsData.cphPctChange';
      const rebuildSeriesKey = 'priceDynamicsData.rebuildPctChange';
      mapping.forEach((m: any) => m.originalName = m.name);

      // Pre-calculate numeric values for each series
      const seriesValueMatrix: (number | null)[][] = mapping.map((m: any) => {
        return chartData.map((d: any) => {
          const rawValue = getNestedValue(d, m.key);
          if (rawValue === null || rawValue === undefined || rawValue === '') {
            return null;
          }
          const numericValue = Number(rawValue);
          return Number.isFinite(numericValue) ? numericValue : null;
        });
      });

      // Determine which series should be shown in legend
      let rebuildHasData = false;
      let cphHasData = false;

      mapping.forEach((m: any, idx: number) => {
        const seriesData = seriesValueMatrix[idx] || [];
        const hasValidData = this.hasMeaningfulSeriesData(seriesData);
        if (m.originalName === 'Rebuild % Change') {
          rebuildHasData = hasValidData;
        }
        if (m.key === cphSeriesKey) {
          cphHasData = hasValidData;
        }
      });

      const { cphLabel: dynamicCphLabel, rebuildLabel: dynamicRebuildLabel } =
        this.resolvePriceDynamicsLegendLabels(cphHasData);


      mapping.forEach((m: any) => {
        if (m.key === cphSeriesKey && dynamicCphLabel !== 'CPH % Change') {
          m.name = dynamicCphLabel;
        }
        if (m.key === rebuildSeriesKey && dynamicRebuildLabel !== 'Rebuild % Change') {
          m.name = dynamicRebuildLabel;
        }
      });

      // Determine NACA context for series visibility logic
      const { regionVal: visRegion, countryVal: visCountry } = this.resolvePriceDynamicsLegendContext();
      const isNacaContext = this.isNacaPriceDynamicsContext(visRegion, visCountry);

      // When both Rebuild and CPH have data:
      //   NACA region  → show CPH, hide Rebuild
      //   Other regions → show Rebuild, hide CPH
      const seriesHasData = mapping.map((m: any, idx: number) => {
        const seriesData = seriesValueMatrix[idx] || [];
        const hasValidData = this.hasMeaningfulSeriesData(seriesData);

        if (m.key === rebuildSeriesKey) {
          // In NACA: hide Rebuild when CPH also has data
          return hasValidData && !(isNacaContext && cphHasData);
        }
        if (m.key === cphSeriesKey) {
          // In non-NACA: hide CPH when Rebuild also has data
          return hasValidData && !(!isNacaContext && rebuildHasData);
        }

        // For other series, always include them
        return true;
      });

      // Filter mapping to only include series that should be displayed
      const filteredMapping = mapping.filter((_m: any, idx: number) => seriesHasData[idx]);
      const filteredSeriesValueMatrix = seriesValueMatrix.filter((_s: any, idx: number) => seriesHasData[idx]);

      const labelOffsetMap = new Map<string, { seriesIndex: number; rank: number; total: number }>();
      for (let dataIndex = 0; dataIndex < xCategories.length; dataIndex++) {
        const cluster = filteredSeriesValueMatrix
          .map((seriesData, seriesIndex) => ({ seriesIndex, value: seriesData[dataIndex] }))
          .filter((entry) => typeof entry.value === 'number')
          .sort((a, b) => Number(a.value) - Number(b.value));

        cluster.forEach((entry, rank) => {
          labelOffsetMap.set(`${entry.seriesIndex}:${dataIndex}`, { seriesIndex: entry.seriesIndex, rank, total: cluster.length });
        });
      }

      // Point labels are shifted above/below lines by value sign, then alternated top/bottom to prevent stacking
      const createPointLabel = (dataArr: (number | null)[], seriesIndex: number) => ({
        show: true,
        fontSize: 12,
        fontWeight: 'bold',
        color: 'inherit',
        position: (params: any) => {
          const dataIndex = params.dataIndex;
          const clusterInfo = labelOffsetMap.get(`${seriesIndex}:${dataIndex}`);
          const rank = clusterInfo?.rank ?? 0;
          const total = clusterInfo?.total ?? 1;
          
          // If only 1-2 items, use normal top/bottom. If 3+, alternate to prevent stacking
          if (total <= 2) {
            const value = Number(params?.value);
            return (!Number.isFinite(value) || value >= 0) ? 'top' : 'bottom';
          }
          // For dense clusters, alternate by rank: even = top, odd = bottom (prevents overlap)
          return (rank % 2 === 0) ? 'top' : 'bottom';
        },
        verticalAlign: 'middle',
        distance: 25,
        offset: [0, -20],
        formatter: (params: any) => {
          const value = Number(params?.value);
          if (!Number.isFinite(value)) return '';
          return `${value.toFixed(1)}%`;
        }
      });

      seriesList = filteredMapping.map((m: any, seriesIndex: number) => {
        const seriesData = filteredSeriesValueMatrix[seriesIndex] || [];
        return {
          name: m.name,
          type: 'line',
          smooth: this.data.chartConfig?.smooth ?? false,
          connectNulls: true,
          data: seriesData,
          itemStyle: m.color ? { color: m.color } : undefined,
          lineStyle: m.color ? { color: m.color } : undefined,
          symbolSize: 8,
          endLabel: {
            ...endLabelStyle,
            show: false,
            fontSize: 15 // matching point label size
          },
          label: createPointLabel(seriesData, seriesIndex)
        };
      });

      // If mapping is empty, provide a fallback or empty list
      if (seriesList.length === 0) {
        seriesList = [{
          name: 'No Data',
          type: 'line',
          data: []
        }];
      }
    } else if (chartData?.category && chartData?.value) {
      // Standard { category, value } shape
      xCategories = chartData.category;
      const seriesData = Array.isArray(chartData.value)
        ? chartData.value.map((value: any) => {
          if (value === null || value === undefined || value === '') {
            return null;
          }
          const numericValue = Number(value);
          return Number.isFinite(numericValue) ? numericValue : null;
        })
        : [];
      seriesList = [{
        name: 'Value',
        type: 'line',
        smooth: this.data.chartConfig?.smooth ?? false,
        connectNulls: false,
        data: seriesData
      }];
    } else {
      console.warn('[CUSTOM LC] Unrecognised data shape:', chartData);
      this.myChart.hideLoading();
      return;
    }

    const preservedLegendSelection = this.buildLegendSelection(
      seriesList,
      this.getCurrentLegendSelection()
    );
    this.latestSeriesList = seriesList;
    const axisRange = this.calculateAxisRange(seriesList, preservedLegendSelection);

    const tooltipTextStyle = {
      fontFamily: 'inherit',
      fontSize: this.data?.chartConfig?.legendFontSize ?? 10,
      fontWeight: 400,
      letterSpacing: '0',
      color: '#000000ff'
    };

    const tooltipValueSuffix = `${this.data?.chartConfig?.tooltipValueSuffix ?? ''}`;
    const tooltipDecimalsRaw = Number(this.data?.chartConfig?.tooltipDecimals);
    const tooltipDecimals = Number.isFinite(tooltipDecimalsRaw) ? tooltipDecimalsRaw : 1;

    const option: any = {
      tooltip: {
        trigger: 'axis',
        textStyle: tooltipTextStyle,
        valueFormatter: (value: any) => {
          const numericValue = Number(value);
          return Number.isFinite(numericValue)
            ? `${numericValue.toFixed(tooltipDecimals)}${tooltipValueSuffix}`
            : `${value ?? ''}`;
        }
      },
      legend: {
        left: 'center',
        right: 10,
        bottom: '2%',
        data: seriesList.map(s => s.name),
        selected: preservedLegendSelection,
        itemGap: 8,
        itemWidth: 14,
        itemHeight: 8,
        textStyle: {
          fontSize: this.data?.chartConfig?.legendFontSize ?? 10,
          fontWeight: 700,
          color: '#000000ff'
        }
      },
      grid: {
        left: 30,
        right: 60, // extra room for labels near chart edge
        top: '8%',
        bottom: 68,
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: xCategories,
        name: 'Year',
        nameLocation: 'middle',
        nameGap: 50,
        nameTextStyle: { fontSize: 14, fontWeight: 'bold' },
        axisLabel: {
          fontSize: 14,
          fontWeight: 'bold',
          interval: 0, // Force all labels to show
          formatter: (value: string) => {
            const clean = `${value ?? ''}`.trim().replace(/\s+/g, ' ');
            const parts = clean.split(' ');
            if (parts.length >= 2) {
              return `${parts[0]}\n${parts.slice(1).join(' ')}`;
            }
            return clean;
          }
        },
        axisTick: { alignWithLabel: true }
      },
      yAxis: {
        type: 'value',
        min: axisRange.min,
        max: axisRange.max,
        scale: true,
        splitNumber: 6,
        name: '% Change',
        nameLocation: 'middle',
        nameGap: 42,
        nameTextStyle: { fontSize: 14, fontWeight: 'bold' },
        axisLabel: {
          fontSize: 14,
          fontWeight: 'bold',
          formatter: (val: number) => `${val}%`
        },
        axisLine: { show: true },
        axisTick: { show: true },
        splitLine: {
          lineStyle: {
            color: '#d5dbe5'
          }
        }
      },
      series: seriesList
    };

    applyRoleVisibilityToOption(option, this.data);
    this.myChart.setOption(option, true);
    this.setupDynamicYAxisScaling();
    this.emitLegendSync(this.getCurrentLegendSelection());
    setTimeout(() => {
      this.myChart.resize();
      this.myChart.hideLoading();
    }, 500);
  }

  private setupDynamicYAxisScaling(): void {
    if (!this.myChart) return;
    this.myChart.off('legendselectchanged');
    this.myChart.on('legendselectchanged', (event: any) => {
      const selected = event?.selected || {};
      this.applyYAxisScaling(selected);
      this.emitLegendSync(selected);
    });
    this.applyYAxisScaling(this.getCurrentLegendSelection());
  }

  private applyYAxisScaling(selectedMap?: Record<string, boolean>): void {
    if (!this.myChart) return;
    const axisRange = this.calculateAxisRange(this.latestSeriesList, selectedMap);
    this.myChart.setOption({
      yAxis: {
        min: axisRange.min,
        max: axisRange.max,
      }
    });
  }

  private calculateAxisRange(
    seriesList: any[],
    selectedMap?: Record<string, boolean>
  ): { min?: number; max?: number } {
    const selectedSeries = (seriesList || []).filter((series: any) => {
      if (!series) return false;
      if (!selectedMap) return true;
      return selectedMap[series.name] !== false;
    });

    const values: number[] = selectedSeries
      .flatMap((series: any) => Array.isArray(series?.data) ? series.data : [])
      .filter((value: any) => typeof value === 'number' && Number.isFinite(value));

    if (values.length === 0) {
      return {};
    }

    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const valueRange = maxValue - minValue;
    const padding = valueRange === 0
      ? Math.max(Math.abs(maxValue) * 0.1, 1)
      : valueRange * 0.12;

    // Dynamically apply padding to both ends based on actual data range
    const computedMin = minValue - padding;
    const computedMax = maxValue + padding;

    return {
      min: Number(computedMin.toFixed(2)),
      max: Number(computedMax.toFixed(2))
    };
  }

  private getCurrentLegendSelection(): Record<string, boolean> | undefined {
    if (!this.myChart) return undefined;
    const option = this.myChart.getOption?.() as any;
    const legend = option?.legend;
    if (!Array.isArray(legend) || legend.length === 0) return undefined;
    const selected = legend[0]?.selected;
    if (!selected || typeof selected !== 'object') return undefined;
    return selected as Record<string, boolean>;
  }

  private buildLegendSelection(
    seriesList: any[],
    previousSelection?: Record<string, boolean>
  ): Record<string, boolean> {
    const selected: Record<string, boolean> = {};
    (seriesList || []).forEach((series: any) => {
      const name = series?.name;
      if (!name) return;
      selected[name] = previousSelection?.[name] !== false;
    });
    return selected;
  }

  private emitLegendSync(selectedMap?: Record<string, boolean>): void {
    const normalizedSelection: Record<string, boolean> = {};
    const seriesNames = (this.latestSeriesList || [])
      .map((series: any) => series?.name)
      .filter((name: any) => typeof name === 'string' && name.trim().length > 0);

    seriesNames.forEach((seriesName: string) => {
      normalizedSelection[seriesName] = selectedMap?.[seriesName] !== false;
    });

    window.dispatchEvent(
      new CustomEvent(LineChartComponent.PRICE_DYNAMICS_LEGEND_EVENT, {
        detail: {
          sourceWidgetId: this.data?.widgetId,
          selectedMap: normalizedSelection,
          seriesNames,
        },
      }),
    );
  }

  private resolvePriceDynamicsLegendLabels(cphHasData: boolean): { cphLabel: string; rebuildLabel: string } {
    let cphLabel = 'CPH % Change';
    let rebuildLabel = 'Rebuild % Change';

    const { regionVal, engineRaw, mineName, countryVal } = this.resolvePriceDynamicsLegendContext();
    const engineVal = this.normalizeEngineLegendValue(engineRaw);

    if (this.isNacaPriceDynamicsContext(regionVal, countryVal)) {
      const nacaLegendBase = this.buildNacaLegendBase(regionVal, engineVal, mineName);

      if (nacaLegendBase) {
        rebuildLabel = nacaLegendBase;
        if (cphHasData && mineName) {
          cphLabel = `${mineName}-CPH`;
        }
      }

      return { cphLabel, rebuildLabel };
    }

    if (regionVal && engineVal) {
      rebuildLabel = engineVal;
    }

    return { cphLabel, rebuildLabel };
  }

  private buildNacaLegendBase(regionVal: string, engineVal: string, mineName: string): string {
    // Format engine value: replace commas with dashes (e.g. "QSK78,T2" -> "QSK78-T2")
    const formattedEngine = engineVal ? engineVal.replace(/,/g, '-') : '';
    const parts = [
      (regionVal || 'NACA').toUpperCase(),
      formattedEngine
    ].filter((value) => Boolean(String(value || '').trim()));
    if (parts.length > 0) {
      return parts.join('-').trim();
    }

    return String(mineName || '').trim();
  }

  private resolvePriceDynamicsLegendContext(): { regionVal: string; engineRaw: unknown; mineName: string; countryVal: string } {
    const liveFilter = this.data?.customPayload?.requestData?.filter || {};
    const payloadFilter = this.currentRequestPayload?.customPayload?.requestData?.filter || {};
    const state = history.state || {};
    const selectedMine = state?.selectedMine || {};
    const fullRow = selectedMine?.__fullRow || {};
    const primaryEngineFromRow = Array.isArray(fullRow?.engine_model)
      ? (fullRow.engine_model[0]?.engine_model_key || fullRow.engine_model[0]?.engine_model || '')
      : '';

    const regionVal = String(
      liveFilter.region ||
      payloadFilter.region ||
      state.selectedRegion ||
      state.region ||
      selectedMine.__regionRaw ||
      selectedMine.region ||
      fullRow.region ||
      '',
    ).trim();

    const engineRaw =
      liveFilter.engine_model ||
      liveFilter.engine_model_key ||
      payloadFilter.engine_model ||
      payloadFilter.engine_model_key ||
      state.selectedEngineWithoutEmission ||
      state.selectedEngineModelKey ||
      state.engine_model ||
      state.engine_model_key ||
      selectedMine.__engineModel ||
      fullRow.engine_model_key ||
      fullRow.engine_model ||
      primaryEngineFromRow ||
      '';

    const mineRaw =
      liveFilter.mine ||
      liveFilter.customer_name ||
      liveFilter.mine_name ||
      payloadFilter.mine ||
      payloadFilter.customer_name ||
      payloadFilter.mine_name ||
      state.selectedMineName ||
      state.mine_name ||
      state.customer_name ||
      selectedMine?.name ||
      selectedMine?.mine ||
      selectedMine?.__mineRaw ||
      fullRow.mine_site ||
      fullRow.mine ||
      fullRow.customer_name ||
      '';

    const mineName = typeof mineRaw === 'string'
      ? mineRaw.trim()
      : String(mineRaw?.name || mineRaw?.mine || mineRaw?.mine_site || '').trim();

    const countryVal = String(
      liveFilter.country ||
      payloadFilter.country ||
      state.country ||
      selectedMine?.__countryRaw ||
      selectedMine?.country ||
      fullRow.country ||
      fullRow.country_name ||
      '',
    ).trim();

    return { regionVal, engineRaw, mineName, countryVal };
  }

  private isNacaPriceDynamicsContext(regionVal: string, countryVal: string): boolean {
    if (regionVal.toUpperCase() === 'NACA') {
      return true;
    }

    const normalizedCountry = countryVal.trim().toUpperCase();
    return ['UNITED STATES', 'UNITED STATES OF AMERICA', 'USA', 'CANADA', 'MEXICO', 'UNITED MEXICAN STATES']
      .includes(normalizedCountry);
  }

  private hasMeaningfulSeriesData(values: Array<number | null>): boolean {
    return values.some((value) => typeof value === 'number' && Number.isFinite(value) && Number(value.toFixed(1)) !== 0);
  }

  private normalizeEngineLegendValue(raw: unknown): string {
    if (!raw) return '';

    let engine = String(raw).trim();
    engine = engine.replace(/^([^,]+)-(T\d+).*/i, '$1,$2');
    engine = engine.replace(/^([^,]+,T\d+).*/i, '$1');
    return engine.trim();
  }

  protected override addResizeListener(): any {
    return super.addResizeListener();
  }

  override ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    super.ngOnDestroy();
  }
}
